import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { storage, db } from "../register/firebase";

const FileUploader = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/login?redirect=upload-analysis-data");
    });
    return () => unsubscribe();
  }, [navigate]);

  const addFiles = useCallback((newFiles) => {
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const uniqueFiles = Array.from(newFiles).filter(
        (file) => !existingNames.has(file.name)
      );
      return [...prev, ...uniqueFiles];
    });
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleUpload = async () => {
    setUploading(true);
    const today = new Date().toISOString().split("T")[0];

    for (const file of files) {
      const path = `analysis_data/${today}/${file.name}`;
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(percent);
          },
          (error) => {
            console.error("Upload failed", error);
            reject(error);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            await setDoc(doc(db, "analysis_data", today, "files", file.name), {
              fileName: file.name,
              url,
              uploadedAt: new Date().toISOString(),
              UID: getAuth().currentUser.uid,
            });

            setUploaded((prev) => [...prev, { name: file.name, url }]);
            resolve();
          }
        );
      });
    }

    setUploading(false);
    setFiles([]);
    setProgress(0);
  };

  const handleRemove = (name) => {
    setFiles((prev) => prev.filter((file) => file.name !== name));
  };

  return (
    <div className="max-w-xl mx-auto p-6 mt-28 mb-16 bg-white shadow-xl rounded-xl border border-gray-200">
      <h1 className="text-2xl font-bold mb-4 text-center text-indigo-600">
        Upload Files
      </h1>

      {/* Drag and Drop Zone */}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`mb-4 p-6 border-2 ${
          dragOver ? "border-indigo-600 bg-indigo-50" : "border-dashed"
        } rounded-lg text-center cursor-pointer transition-all relative block`}
      >
        <p className="text-sm text-gray-600">
          Drag and drop files here or click to select
        </p>
        <input
          type="file"
          multiple
          onChange={(e) => addFiles(e.target.files)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>

      {files.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">Files to Upload</h2>
          <ul className="space-y-2">
            {files.map((file) => (
              <li
                key={file.name}
                className="flex justify-between items-center text-sm border p-2 rounded-md"
              >
                <div>
                  <span className="font-medium">{file.name}</span>{" "}
                  <span className="text-gray-500 text-xs">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  onClick={() => handleRemove(file.name)}
                  className="text-red-600 hover:underline text-xs"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!files.length || uploading}
        className="w-full bg-[#2d3191] text-white font-medium py-2 rounded-lg hover:bg-green-700 transition duration-300 disabled:opacity-50"
      >
        Upload
      </button>

      {/* Uploading Dialog */}
      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 text-center shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Uploading...</h2>
            <div className="w-64 bg-gray-200 h-4 rounded-full overflow-hidden">
              <div
                className="bg-indigo-600 h-4"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-gray-700">{progress}% completed</p>
          </div>
        </div>
      )}

      {uploaded.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Uploaded Files</h2>
          <ul className="space-y-2 text-sm">
            {uploaded.map((file) => (
              <li key={file.name}>
                <span className="font-medium text-green-700">{file.name}</span>:
                completed
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
